import { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/stores/authStore';
import { useCurrentFiscalYear } from '@/hooks/queries/useJournalEntries';
import { useChartOfAccounts } from '@/hooks/queries/useAccounting';
import { useCreateJournalEntry } from '@/hooks/queries/useJournalEntries';
import { JOURNAL_TYPE_LABELS, type JournalType } from '@/types/accounting';
import { Plus, Trash2, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// =====================================================
// VALIDATION SCHEMA
// =====================================================

const journalEntryLineSchema = z.object({
  account_code: z.string().min(1, 'Compte requis / الحساب مطلوب'),
  debit: z.coerce.number().min(0, 'Débit doit être >= 0').default(0),
  credit: z.coerce.number().min(0, 'Crédit doit être >= 0').default(0),
  description: z.string().optional(),
});

const journalEntrySchema = z
  .object({
    entry_date: z.string().min(1, 'Date requise / التاريخ مطلوب'),
    journal_type: z.enum([
      'general',
      'sales',
      'purchases',
      'bank',
      'cash',
      'payroll',
      'operations',
    ]),
    description: z.string().optional(),
    lines: z.array(journalEntryLineSchema).min(2, 'Minimum 2 lignes requises'),
  })
  .refine(
    (data) => {
      // Ensure each line has EITHER debit OR credit (not both, not neither)
      return data.lines.every(
        (line) =>
          (line.debit > 0 && line.credit === 0) ||
          (line.credit > 0 && line.debit === 0)
      );
    },
    {
      message:
        'Chaque ligne doit avoir SOIT débit SOIT crédit (pas les deux) / كل سطر يجب أن يحتوي إما على مدين أو دائن',
      path: ['lines'],
    }
  );

type JournalEntryFormData = z.infer<typeof journalEntrySchema>;

// =====================================================
// COMPONENT
// =====================================================

interface JournalEntryFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function JournalEntryForm({ open, onOpenChange }: JournalEntryFormProps) {
  const { company, user } = useAuthStore();
  const [postImmediately, setPostImmediately] = useState(false);

  const { data: currentFiscalYear } = useCurrentFiscalYear(company?.id || '');
  const { data: accounts = [] } = useChartOfAccounts(company?.id || '', {
    isActive: true,
  });
  const createEntry = useCreateJournalEntry();

  const form = useForm<JournalEntryFormData>({
    resolver: zodResolver(journalEntrySchema),
    defaultValues: {
      entry_date: new Date().toISOString().split('T')[0],
      journal_type: 'general',
      description: '',
      lines: [
        { account_code: '', debit: 0, credit: 0, description: '' },
        { account_code: '', debit: 0, credit: 0, description: '' },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'lines',
  });

  // Calculate totals
  const watchLines = form.watch('lines');
  const totalDebit = watchLines.reduce((sum, line) => sum + (line.debit || 0), 0);
  const totalCredit = watchLines.reduce((sum, line) => sum + (line.credit || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const onSubmit = async (data: JournalEntryFormData) => {
    if (!company?.id || !user?.id || !currentFiscalYear?.id) {
      toast.error('Erreur: Société, utilisateur ou exercice fiscal manquant');
      return;
    }

    if (!isBalanced) {
      toast.error(
        `Écriture non équilibrée: Débit ${totalDebit.toFixed(2)} != Crédit ${totalCredit.toFixed(2)}`
      );
      return;
    }

    try {
      await createEntry.mutateAsync({
        entry: {
          company_id: company.id,
          fiscal_year_id: currentFiscalYear.id,
          entry_date: data.entry_date,
          journal_type: data.journal_type,
          reference_type: 'manual',
          description: data.description,
          status: postImmediately ? 'posted' : 'draft',
          created_by: user.id,
        },
        lines: data.lines.map((line) => ({
          account_code: line.account_code,
          debit: line.debit || 0,
          credit: line.credit || 0,
          description: line.description,
        })),
      });

      toast.success(
        postImmediately
          ? '✅ Écriture comptabilisée avec succès'
          : '✅ Brouillon sauvegardé avec succès'
      );
      form.reset();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(`Erreur: ${error.message}`);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Account selector options (only active accounts that allow direct posting)
  const accountOptions = accounts.filter(
    (acc) => acc.is_active && !acc.is_system_account
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            قيد يومية جديد / Nouvelle écriture comptable
          </DialogTitle>
          <DialogDescription>
            Créez une écriture en partie double avec validation d'équilibre automatique
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Header Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="entry_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date / التاريخ *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="journal_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type de journal / نوع اليومية *</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(JOURNAL_TYPE_LABELS).map(([key, labels]) => (
                          <SelectItem key={key} value={key}>
                            {labels.fr} / {labels.ar}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div>
                <label className="text-sm font-medium">Exercice fiscal / السنة المالية</label>
                <Input
                  value={currentFiscalYear?.year_code || 'Aucun exercice ouvert'}
                  disabled
                  className="bg-muted mt-2"
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optionnelle) / الوصف</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Description de l'écriture..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Journal Lines */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Lignes / السطور</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    append({ account_code: '', debit: 0, credit: 0, description: '' })
                  }
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une ligne
                </Button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-muted p-3 grid grid-cols-12 gap-2 text-sm font-medium">
                  <div className="col-span-4">Compte / الحساب</div>
                  <div className="col-span-3">Description</div>
                  <div className="col-span-2 text-right">Débit / مدين</div>
                  <div className="col-span-2 text-right">Crédit / دائن</div>
                  <div className="col-span-1"></div>
                </div>

                <div className="divide-y">
                  {fields.map((field, index) => (
                    <div key={field.id} className="p-3 grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-4">
                        <FormField
                          control={form.control}
                          name={`lines.${index}.account_code`}
                          render={({ field }) => (
                            <FormItem>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Sélectionner..." />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {accountOptions.map((account) => (
                                    <SelectItem
                                      key={account.id}
                                      value={account.account_code}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="font-mono font-bold">
                                          {account.account_code}
                                        </span>
                                        <span className="text-xs">
                                          {account.account_name}
                                        </span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="col-span-3">
                        <FormField
                          control={form.control}
                          name={`lines.${index}.description`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="Description..." {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="col-span-2">
                        <FormField
                          control={form.control}
                          name={`lines.${index}.debit`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  className="text-right"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="col-span-2">
                        <FormField
                          control={form.control}
                          name={`lines.${index}.credit`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  className="text-right"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="col-span-1 flex items-center justify-center">
                        {fields.length > 2 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals Row */}
                <div className="bg-muted/50 p-3 grid grid-cols-12 gap-2 font-bold border-t-2">
                  <div className="col-span-7 text-right">TOTAL:</div>
                  <div className="col-span-2 text-right text-blue-600">
                    {formatCurrency(totalDebit)} DA
                  </div>
                  <div className="col-span-2 text-right text-green-600">
                    {formatCurrency(totalCredit)} DA
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    {isBalanced ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                </div>
              </div>

              {/* Balance Indicator */}
              <div className="flex items-center justify-center p-4">
                {isBalanced ? (
                  <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 px-4 py-2">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    ✅ Écriture équilibrée / القيد متوازن
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 px-4 py-2">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    ⚠ Non équilibré - Différence:{' '}
                    {formatCurrency(Math.abs(totalDebit - totalCredit))} DA
                  </Badge>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset();
                  onOpenChange(false);
                }}
                disabled={createEntry.isPending}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="outline"
                onClick={() => setPostImmediately(false)}
                disabled={!isBalanced || createEntry.isPending}
              >
                {createEntry.isPending && !postImmediately ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                💾 Sauvegarder brouillon
              </Button>
              <Button
                type="submit"
                onClick={() => setPostImmediately(true)}
                disabled={!isBalanced || createEntry.isPending}
                className="bg-gradient-to-r from-emerald-500 to-teal-600"
              >
                {createEntry.isPending && postImmediately ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                ✅ Comptabiliser
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
